<template>
	<SuiDataTableRow class="task-list-row-add-task" :class="classAddons">
		<SuiDataTableCell :colspan class="task-list-row-add-task__cell">
			&nbsp;
		</SuiDataTableCell>
		<SuiDataTableCell
			:colspan="showSubtasks ? 2 : 1"
			class="task-list-row-add-task__cell task-list-row-add-task__cell--input"
		>
			<div class="task-list-row-add-task__actions">
				<SuiInput
					v-if="isEditModeActive"
					ref="$suiInput"
					v-model="name"
					:placeholder="$t('task_list_add_inline_task_placeholder')"
					class="task-list-row-add-task__input"
					type-icon="icon-task-new-16"
					size="small"
					@keydown.esc="onEsc()"
					@blur="onBlur"
					@keydown.enter="onSubmit()"
				/>
				<SuiButton
					v-else
					variant="unstyled"
					class="task-list-row-add-task__button"
					@click="onStartEditMode()"
				>
					<SuiIcon name="icon-task-new-16" />
					{{ addTaskLabelTranslation }}
				</SuiButton>

				<TaskListRowAddTaskQuickActions
					:project-phase-id="projectPhaseId"
					:task-id="taskId"
				/>
			</div>
		</SuiDataTableCell>

		<template v-if="showTotals">
			<template v-for="cell in sortableOrderedCells" :key="cell.columnId">
				<SuiDataTableCell
					v-if="cell.columnId === TABLE_COLUMN.Remaining && tasks"
					align="right"
					is-number
					class="task-list-row-add-task__cell"
					:aria-label="$t('total')"
				>
					<strong>
						{{
							formatDuration(
								calculateTotalDuration('totalDurationRemaining')
							)
						}}
					</strong>
				</SuiDataTableCell>
				<SuiDataTableCell
					v-else-if="cell.columnId === TABLE_COLUMN.Planned && tasks"
					align="right"
					is-number
					class="task-list-row-add-task__cell"
					:aria-label="$t('total')"
				>
					<strong>
						{{
							formatDuration(
								calculateTotalDuration('totalDurationPlanned')
							)
						}}
					</strong>
				</SuiDataTableCell>
				<SuiDataTableCell
					v-else-if="cell.columnId === TABLE_COLUMN.Done && tasks"
					align="right"
					is-number
					class="task-list-row-add-task__cell"
					:aria-label="$t('total')"
				>
					<strong>
						{{
							formatDuration(
								calculateTotalDuration('totalDurationDone')
							)
						}}
					</strong>
				</SuiDataTableCell>
				<SuiDataTableCell v-else class="task-list-row-add-task__cell" />
			</template>

			<SuiDataTableCell
				:colspan="REMAINING_COLSPAN"
				class="task-list-row-add-task__cell"
			/>
		</template>

		<SuiDataTableCell
			v-else
			class="task-list-row-add-task__cell"
			:colspan="REMAINING_COLSPAN"
		/>
	</SuiDataTableRow>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import {
	SuiDataTableRow,
	SuiDataTableCell,
	SuiIcon,
	SuiButton,
	SuiInput,
	type SuiDataTableColumnType,
} from '@developers/scoro-ui';
import { useFormatter, useTranslation } from '@/composables';
import { type Task, type ProjectPhase, TYPES } from '@/generated/graphql';
import { useTaskListStore } from '../store';
import { wait } from '@/utils/misc';
import TaskListRowAddTaskQuickActions from './TaskListRowAddTaskQuickActions.vue';
import { useTaskListRowCells } from './composables';
import { TABLE_COLUMN, SORTABLE_COLUMNS } from '../constants';
import { type TasListTaskOrLimited } from '../types';

// Large colspan value to fill remaining columns (HTML will cap at actual remaining columns)
const REMAINING_COLSPAN = 99;

const emit = defineEmits<{
	(e: 'add', name: string): void;
}>();

export type TaskListRowAddTaskProps = {
	taskId?: Task['id'];
	projectPhaseId?: ProjectPhase['id'];
	isSubtask?: boolean;
	addTaskLabel?: string;
	showSubtasks?: boolean;
	showTotals?: boolean;
	colDefinition?: SuiDataTableColumnType[];
	tasks?: TasListTaskOrLimited[];
};

const {
	taskId = undefined,
	projectPhaseId = undefined,
	isSubtask = false,
	addTaskLabel = undefined,
	showSubtasks = false,
	showTotals = false,
	colDefinition = [],
	tasks = [],
} = defineProps<TaskListRowAddTaskProps>();

const { t } = useTranslation();
const taskListStore = useTaskListStore();
const { shortDuration } = useFormatter();

const formatDuration = (duration: number) => {
	return duration ? shortDuration(duration)[0].value : '';
};

const $suiInput = ref<typeof SuiInput>();
const isEditMode = ref(false);
const name = ref('');

const addTaskLabelTranslation = computed(
	() => addTaskLabel || t('task_list_add_inline_task')
);

const colDefinitionRef = computed(() => colDefinition);

const { orderedCells } = useTaskListRowCells({
	colDefinition: colDefinitionRef,
});

const sortableOrderedCells = computed(() =>
	orderedCells.value.filter((cell) =>
		SORTABLE_COLUMNS.includes(cell.columnId)
	)
);

const colspan = computed(() => {
	const titleIndex = orderedCells.value.findIndex(
		(cell) => cell.columnId === TABLE_COLUMN.Title
	);
	return titleIndex !== -1 ? titleIndex : 0;
});

const focusInput = () => {
	$suiInput.value?.$refs.input.focus();
};

const onStartEditMode = () => {
	isEditMode.value = true;

	nextTick(() => focusInput());
};

const onSubmit = () => {
	if (name.value.length > 0) {
		emit('add', name.value);
		name.value = '';
	}
	if ($suiInput.value?.$refs.input) {
		$suiInput.value?.$refs.input.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
		});
	}
};

const exitEditMode = () => {
	isEditMode.value = false;
	taskListStore.editModeTaskId = undefined;
};

const onBlur = (_inputValue: string, $event: FocusEvent) => {
	const relatedTarget = $event.relatedTarget;

	if (
		relatedTarget instanceof HTMLElement &&
		relatedTarget.classList.contains(
			'task-list-row-add-task-quick-actions__trigger'
		)
	) {
		return;
	}

	if (name.value === '') {
		exitEditMode();
	}
};

const onEsc = () => {
	exitEditMode();
	name.value = '';
};

const isEditModeActive = computed(() => {
	return (
		isEditMode.value ||
		(taskListStore.editModeTaskId &&
			taskId === taskListStore.editModeTaskId)
	);
});

const calculateTotalDuration = (
	property:
		| 'totalDurationRemaining'
		| 'totalDurationPlanned'
		| 'totalDurationDone'
): number => {
	if (!tasks) {
		return 0;
	}

	return tasks.reduce(
		(acc: number, task: TasListTaskOrLimited) =>
			task.__typename === TYPES.Task ? acc + (task[property] || 0) : acc,
		0
	);
};

onMounted(async () => {
	await wait(100);

	if (isEditModeActive.value) {
		focusInput();
	}
});

watch(isEditModeActive, async (newVal) => {
	if (newVal) {
		await wait(100);
		focusInput();
	}
});

const classAddons = computed(() => ({
	'task-list-row-add-task--subtask': isSubtask,
}));
</script>

<style lang="scss">
.task-list-row-add-task {
	&__cell {
		--sui-data-table-cell-border-bottom: 0;
		--sui-data-table-cell-padding: 1.5rem 1.5rem 1.5rem 0;
	}

	&__actions {
		display: flex;
		align-items: center;
		gap: 1rem;
	}

	&__button.task-list-row-add-task__button {
		--sui-button-font-size: 1.625rem;
		--sui-button-icon-color: #{$neutral-70};
		--sui-button-text-color: #{$neutral-70};
		--sui-button-h-padding: 2rem;

		gap: 0.5rem;
		font-weight: $scoro-fw-normal;
		justify-content: start;

		&:hover {
			--sui-button-icon-color: #{$neutral-80};
			--sui-button-text-color: #{$neutral-80};
			--sui-button-hover-background-color: #{$neutral-10};
		}
	}

	&__input {
		max-width: 50rem;

		.sui-input__icon--type {
			left: calc(1.5rem - 1px);
		}

		.sui-input__field {
			padding-left: calc(4.5rem - 1px);
		}
	}

	&--subtask {
		.task-list-row-add-task__cell--input {
			padding-left: 2rem;
		}

		+ .task-list-row-add-task {
			--sui-data-table-cell-border-top: 1px solid #{$neutral-20};
		}
	}
}
</style>
